import { useEffect, useRef, useState } from 'react';
import { IonContent, IonPage, IonSpinner } from '@ionic/react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TopBar } from '../components/ui/TopBar';
import { fireConfetti } from '../utils/confetti';
import { ListingLocationMap } from '../components/listings/ListingLocationMap';
import { OrderStatusBreadcrumb } from '../components/orders/OrderStatusBreadcrumb';
import { VerificationBubbles } from '../components/orders/VerificationBubbles';
import { OnMyWayButton } from '../components/orders/OnMyWayButton';
import { OpenInMapsButton } from '../components/orders/OpenInMapsButton';
import { ReviewModal } from '../components/activity/ReviewModal';
import { dismissOrderToast } from '../components/activity/ActivityToast';
import { RejectOrderModal } from '../components/orders/RejectOrderModal';
import {
  useOrderDetail,
  useCancelOrder,
  useCancelPreview,
  useVerifyOrder,
  usePatchOrderStatus,
} from '../hooks/useOrders';
import { getPaymentStatus } from '../services/orderService';
import { DishInfoCard } from '../components/listings/DishInfoCard';
import { OrderDetailCard } from '../components/orders/OrderDetailCard';
import { formatPriceCents } from '../utils/currency';
import { presentWindow } from '../utils/timeWindow';
import type { OrderStatus } from '../types/order';

// Every non-terminal post-payment state. Pre-acceptance states void the
// Stripe authorization (no charge); post-acceptance partial-captures
// `payment.AmountFees`. The cancel preview endpoint owns the fee math —
// the modal renders whatever it returns.
const BUYER_CANCELABLE: OrderStatus[] = [
  'held',
  'confirmed',
  'paid',
  'received',
  'accepted',
  'ready_to_pickup',
];

interface OrderNavState {
  paymentId?: string;
}

export const OrderInProgressPage: React.FC = () => {
  const { t } = useTranslation('orders');
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation<OrderNavState | undefined>();
  const paymentId = location.state?.paymentId;
  const { data, isLoading, error } = useOrderDetail(id);

  // On this order's page → the user is looking at it; clear any toast for it.
  useEffect(() => {
    dismissOrderToast(id);
  }, [id]);
  const cancelMutation = useCancelOrder();
  const verifyMutation = useVerifyOrder();
  const statusMutation = usePatchOrderStatus();

  const [verifyError, setVerifyError] = useState('');
  const [showReview, setShowReview] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const cancelPreviewQuery = useCancelPreview(id, showCancelModal);
  const prevStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (prevStatusRef.current !== undefined && prevStatusRef.current !== 'completed' && data?.status === 'completed') {
      fireConfetti();
    }
    prevStatusRef.current = data?.status;
  }, [data?.status]);

  const isWaitingForPayment = data?.status === 'held' || data?.status === 'confirmed';

  // The backend returns `orderedBy` when the caller is the lister, and `lister`
  // when the caller is the buyer. We use this to determine the role.
  const isLister = !!data?.orderedBy;
  // Drop-off: the chef drives to the eater, so there's no On My Way to send, no
  // chef address or map to show, and "ready" means the chef's run has started.
  const isDropoff = data?.fulfillmentMethod === 'dropoff';
  const chefName = data?.lister?.businessName || data?.lister?.displayName;

  // Listers manage orders from the listing details page — redirect there,
  // carrying the order id as `?order=` so the listing page can scroll to and
  // highlight the specific order card this notification was about. Query param
  // (not router state) so it survives the logged-out push-tap → secret-login
  // full reload.
  useEffect(() => {
    if (isLister && data?.listing?.id) {
      history.replace(`/listing/${data.listing.id}?order=${id}`);
    }
  }, [isLister, data?.listing?.id, id, history]);

  // Handoff-confirmed orders drop out of the cancelable set even though their
  // status is still `ready_to_pickup`. That status covers both "food is still on
  // the chef's counter" and "food is in the eater's hands, handshake unfinished";
  // either handshake half being set marks the second, where a cancel would
  // refund food the eater already has (the partial-capture path keeps fees
  // only). The backend enforces the same rule on PATCH and on cancel-preview —
  // this just keeps the button from offering what the server will refuse.
  const handoffStarted =
    data?.status === 'ready_to_pickup' && (!!data.listerConfirmed || !!data.ordererConfirmed);
  // A drop-off at ready_to_pickup is out for delivery: the chef has already
  // left with the food, so the eater's cancel closes (backend enforces it too).
  const dropoffOnTheWay =
    data?.fulfillmentMethod === 'dropoff' && data.status === 'ready_to_pickup';
  const canCancel =
    data &&
    !isLister &&
    BUYER_CANCELABLE.includes(data.status) &&
    !handoffStarted &&
    !dropoffOnTheWay;

  const obfuscatedCenter = (() => {
    if (!data?.location?.obfuscatedLocation) return null;
    const [lat, lng] = data.location.obfuscatedLocation.split(',').map(Number);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  })();

  const hasPreciseLocation =
    data?.location?.listingLocation?.lat != null &&
    data?.location?.listingLocation?.lon != null;

  // Poll the payment endpoint as a catch-up when stuck on confirmed.
  useEffect(() => {
    if (data?.status !== 'confirmed' || !paymentId) return;
    const interval = setInterval(async () => {
      try {
        await getPaymentStatus(paymentId);
      } catch {
        // Non-fatal
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [data?.status, paymentId]);

  const handleCancelConfirm = (reason: string) => {
    if (!id) return;
    // `held` still uses DELETE → status becomes `abandoned` (UI-hidden). All
    // other states go through PATCH → `canceled` (visible in history) and run
    // through the same Stripe void/partial-capture branch the backend already
    // owns. The fee shown in the modal came from the preview endpoint, which
    // mirrors that same mapping.
    if (data?.status === 'held') {
      cancelMutation.mutate({ id }, {
        onSuccess: () => {
          setShowCancelModal(false);
          history.replace('/');
        },
      });
    } else {
      statusMutation.mutate(
        { id, status: 'canceled', rejectionReason: reason },
        {
          onSuccess: () => {
            setShowCancelModal(false);
            history.replace('/');
          },
        },
      );
    }
  };

  const handleVerify = (code: number) => {
    if (!id) return;
    setVerifyError('');
    verifyMutation.mutate(
      { id, code },
      {
        onSuccess: (result) => {
          if (!result.success) {
            setVerifyError(t('verify.wrongCode'));
          }
        },
      },
    );
  };

  return (
    <IonPage>
      <TopBar />
      <IonContent>
        <div className="mx-auto w-full max-w-md p-4 space-y-5">
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <IonSpinner name="crescent" />
            </div>
          )}

          {error && (
            <p className="text-center text-sm text-brand-danger">
              {t('inProgress.errorLoad')}
            </p>
          )}

          {data && !isLister && (
            <>
              {/* Listing card */}
              {data.listing && (
                <DishInfoCard
                  listing={{
                    title: data.listing.title,
                    imageUrl: data.listing.imageUrl ?? null,
                    thumbnailUrl: data.listing.thumbnailUrl ?? null,
                    // `timeWindow` is the envelope over BOTH methods (pickup
                    // start → drop-off end). Show the window this order uses.
                    timeWindow:
                      presentWindow(isDropoff ? data.listing.dropoffWindow : data.listing.pickupWindow) ??
                      data.listing.timeWindow ??
                      null,
                    listerSummary: data.lister ?? null,
                  }}
                  disableExpand
                />
              )}

              {/* Buyer, pickup handoff: an optional heads-up to the chef, shown
                  ABOVE the pickup code rather than instead of it. The code is
                  ungated now, so this no longer unlocks anything — it only buys
                  the chef lead time. Suppressed once they've pressed Handed Off,
                  since there is no lead time left to give and the push would
                  land on someone who already handed the food over. */}
              {!isDropoff && data.status === 'ready_to_pickup' && !data.onMyWay && !data.listerConfirmed && (
                <div className="rounded-xl border border-border p-4">
                  <OnMyWayButton orderId={id} />
                </div>
              )}

              {/* Buyer, drop-off: where the run stands. Before it starts the
                  chef hasn't left yet; once it has, the food is on the way.
                  Drops off once either side confirms the handoff — the food
                  has arrived by then. */}
              {isDropoff && data.status === 'accepted' && (
                <div className="rounded-xl border border-border p-4 text-center text-sm text-muted-foreground">
                  {t('inProgress.dropoffScheduled')}
                </div>
              )}
              {isDropoff &&
                data.status === 'ready_to_pickup' &&
                !data.listerConfirmed &&
                !data.ordererConfirmed && (
                  <div className="rounded-xl bg-brand-primary/10 px-4 py-3 text-center text-sm font-medium text-brand-primary">
                    {chefName
                      ? t('inProgress.outForDelivery', { name: chefName })
                      : t('inProgress.outForDeliveryNoName')}
                  </div>
                )}

              {/* Buyer: verification number at ready_to_pickup.
                  Once the orderer confirms, the confirm button flips to the disabled
                  "waiting on the chef" state until the lister completes the
                  handshake. `handedOff` covers the reverse order — the lister
                  confirmed first, so the read-aloud framing is stale. */}
              {data.verification?.code != null && (
                <div className="rounded-xl border border-border p-4 space-y-2">
                  <VerificationBubbles
                    code={data.verification.code}
                    onSelect={handleVerify}
                    isSubmitting={verifyMutation.isPending}
                    lister={data.lister}
                    confirmed={!!data.ordererConfirmed}
                    handedOff={!!data.listerConfirmed}
                  />
                  {verifyError && (
                    <p className="text-center text-sm text-brand-danger">{verifyError}</p>
                  )}
                </div>
              )}

              {/* Status breadcrumb — skip for pre-payment statuses */}
              {!isWaitingForPayment && (
                <OrderStatusBreadcrumb status={data.status} fulfillmentMethod={data.fulfillmentMethod} />
              )}

              {/* Completed message + review — kept directly under the breadcrumb,
                  ABOVE the address card and map. Below them it landed off-screen
                  on a phone and eaters simply never saw the review prompt. */}
              {data.status === 'completed' && (
                <div className="rounded-xl border border-brand-primary/30 bg-brand-primary/5 p-4 text-center space-y-3">
                  <p className="font-semibold text-brand-primary">{t('inProgress.completedTitle')}</p>
                  {data.reviewEligible && !data.hasReviewed && (
                    <button
                      type="button"
                      onClick={() => setShowReview(true)}
                      className="w-full rounded-lg bg-brand-primary py-2 text-sm font-semibold text-white active:opacity-80"
                    >
                      {t('card.leaveReview')}
                    </button>
                  )}
                  {data.hasReviewed && (
                    <p className="text-sm text-muted-foreground">{t('inProgress.reviewThanks')}</p>
                  )}
                </div>
              )}

              {data.status === 'completed' && id && (
                <ReviewModal
                  isOpen={showReview}
                  onClose={() => setShowReview(false)}
                  orderId={id}
                />
              )}

              {isWaitingForPayment && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2">
                  <IonSpinner name="crescent" className="h-4 w-4" />
                  {t('inProgress.processingPayment')}
                </div>
              )}

              {/* Drop-off: the address the food is coming to. The backend never
                  sends the chef's address on a drop-off order. */}
              {isDropoff && data.location?.deliveryAddress && (
                <div className="rounded-xl border border-border p-4 text-sm space-y-1">
                  <p className="font-medium">{t('inProgress.deliveryAddressLabel')}</p>
                  <p>
                    {data.location.deliveryAddress.street1 || data.location.deliveryAddress.address}
                  </p>
                  {data.location.deliveryAddress.street2 && (
                    <p>{data.location.deliveryAddress.street2}</p>
                  )}
                  <p className="text-muted-foreground">
                    {[
                      data.location.deliveryAddress.city,
                      data.location.deliveryAddress.stateProvince,
                      data.location.deliveryAddress.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </div>
              )}

              {/* Location reveal — shown when status >= accepted */}
              {data.location?.listingLocation && (
                <div className="rounded-xl border border-border p-4 text-sm space-y-1">
                  <p className="font-medium">{t('inProgress.pickupLocationLabel')}</p>
                  {data.location.listingLocation.street1 && (
                    <p>{data.location.listingLocation.street1}</p>
                  )}
                  {data.location.listingLocation.street2 && (
                    <p>{data.location.listingLocation.street2}</p>
                  )}
                  <p className="text-muted-foreground">
                    {[
                      data.location.listingLocation.city,
                      data.location.listingLocation.stateProvince,
                      data.location.listingLocation.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {data.distance != null && (
                    <p className="text-xs text-muted-foreground">
                      {data.driveTime != null
                        ? t('inProgress.distanceAndDrive', { miles: data.distance.toFixed(1), minutes: Math.round(data.driveTime / 60) })
                        : t('inProgress.distance', { miles: data.distance.toFixed(1) })}
                    </p>
                  )}
                  {/* Safe here because the card itself only renders once the API
                      returns `listingLocation` — the reveal at status >= accepted.
                      The obfuscated pre-acceptance state has no address to open. */}
                  <OpenInMapsButton
                    destination={data.location.listingLocation}
                    label={data.listing?.title}
                    className="mt-3"
                  />
                </div>
              )}

              {/* Map: obfuscated circle pre-acceptance, precise pin once the real address is available.
                  `key` forces a remount on the transition — ListingLocationMap initializes once per mount. */}
              {isDropoff ? null : hasPreciseLocation ? (
                <ListingLocationMap
                  key="precise"
                  center={{
                    lat: data.location!.listingLocation!.lat!,
                    lng: data.location!.listingLocation!.lon!,
                  }}
                  enableStreetView
                />
              ) : (
                obfuscatedCenter && (
                  <ListingLocationMap
                    key="obfuscated"
                    center={obfuscatedCenter}
                    showCircle
                  />
                )
              )}

              {/* Terminal-cancellation summary for the orderer — shows what
                  was charged (if anything) vs released. Captured > 0 means
                  the eater hit a post-acceptance cancellation fee; captured
                  === 0 covers both pre-acceptance voids and lister-driven
                  full refunds (indistinguishable from order data alone). */}
              {(data.status === 'canceled' || data.status === 'expired') && (
                <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-2">
                  <p className="font-semibold">
                    {t(
                      data.status === 'expired'
                        ? 'inProgress.expiredTitle'
                        : data.canceledBy === 'lister'
                          ? 'inProgress.canceledByChefTitle'
                          : data.canceledBy === 'system'
                            ? 'inProgress.canceledDropoffClosedTitle'
                            : 'inProgress.canceledTitle',
                    )}
                  </p>
                  {(data.amountCaptured ?? 0) > 0 ? (
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">{t('card.cancellationFee')}</span>
                        <span className="font-semibold text-brand-danger">
                          {formatPriceCents(data.amountCaptured)}
                        </span>
                      </div>
                      {(data.amountTotal ?? 0) > (data.amountCaptured ?? 0) && (
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{t('inProgress.releasedBack')}</span>
                          <span>
                            {formatPriceCents((data.amountTotal ?? 0) - (data.amountCaptured ?? 0))}
                          </span>
                        </div>
                      )}
                      {(data.amountRefunded ?? 0) > 0 && (
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{t('inProgress.refundedLabel')}</span>
                          <span>{formatPriceCents(data.amountRefunded)}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('inProgress.fullReleased')}</p>
                  )}
                </div>
              )}

              {data.status === 'no_show' && (
                <div className="rounded-xl border border-brand-danger/30 bg-brand-danger/5 p-4 space-y-2">
                  <p className="font-semibold text-brand-danger">{t('inProgress.noShowTitle')}</p>
                  {data.amountCaptured ? (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('inProgress.noShowFull')}</span>
                      <span className="font-semibold">{formatPriceCents(data.amountCaptured)}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('inProgress.noShowNoCharge')}</p>
                  )}
                </div>
              )}

              {/* Buyer: cancel */}
              {canCancel && (
                <button
                  type="button"
                  onClick={() => setShowCancelModal(true)}
                  disabled={cancelMutation.isPending || statusMutation.isPending}
                  className="flex w-full items-center justify-center gap-2 rounded-xl btn-outline border-brand-danger py-3 text-sm font-semibold text-brand-danger active:opacity-80 disabled:opacity-50"
                >
                  {cancelMutation.isPending || statusMutation.isPending ? (
                    <>
                      <IonSpinner name="crescent" className="h-4 w-4" />
                      {t('inProgress.canceling')}
                    </>
                  ) : (
                    t('inProgress.cancelCta')
                  )}
                </button>
              )}

              {canCancel && (
                <RejectOrderModal
                  isOpen={showCancelModal}
                  onClose={() => setShowCancelModal(false)}
                  onConfirm={handleCancelConfirm}
                  isPending={cancelMutation.isPending || statusMutation.isPending}
                  mode="eater-cancel"
                  feePreview={cancelPreviewQuery.data}
                  feePreviewLoading={cancelPreviewQuery.isLoading}
                />
              )}

              <OrderDetailCard
                orderNumber={data.orderNumber}
                quantity={data.quantity}
                fulfillmentMethod={data.fulfillmentMethod}
                pickupTime={data.intendedPickupTime}
                dropoffWindow={data.listing?.dropoffWindow}
                orderedAt={data.createdAt}
                timezone={data.listing?.timeWindow?.timezone}
                total={formatPriceCents(data.totalPaid)}
              />
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};
